"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { useState } from "react";
import { languageLabels } from "~/lib/languages/utilities";
import { routes } from "~/lib/routes";

type Stage = "login" | "new_password";

const ACCENT = "oklch(0.58 0.19 252)";

function setLocaleCookie(locale: string) {
  // biome-ignore lint/suspicious/noDocumentCookie: ui_locale is a non-sensitive client-readable cookie
  document.cookie = `ui_locale=${locale};path=/;max-age=31536000;samesite=lax`;
}

const inputStyle: React.CSSProperties = {
  width: "100%",
  boxSizing: "border-box",
  padding: "13px 15px",
  fontSize: 15,
  fontFamily: "inherit",
  color: "#1c1b22",
  background: "#ffffff",
  border: "1px solid #e0e0e6",
  borderRadius: 10,
  outline: "none",
};

const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: 13,
  fontWeight: 500,
  color: "#3a3a42",
  marginBottom: 8,
};

const btnStyle: React.CSSProperties = {
  width: "100%",
  padding: 14,
  fontSize: 15.5,
  fontWeight: 700,
  fontFamily: "inherit",
  color: "#ffffff",
  background: ACCENT,
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  letterSpacing: "0.02em",
};

const PAGE_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;500;700;900&family=IBM+Plex+Mono:wght@400;500&display=swap');
  * { box-sizing: border-box; }
  html, body { margin: 0; height: 100%; }
  .lf-input::placeholder { color: #b0b0b8; }
  .lf-input:focus { border-color: ${ACCENT}; box-shadow: 0 0 0 3px rgba(0,0,0,0.04); }
  .lf-btn:hover:not(:disabled) { filter: brightness(1.07); }
  @media (max-width: 820px) { .lf-brand { display: none !important; } }
`;

export default function AuthPage() {
  const t = useTranslations("AuthPage");
  const router = useRouter();
  const locale = useLocale();

  const [stage, setStage] = useState<Stage>("login");
  const [pendingEmail, setPendingEmail] = useState("");
  const [pendingSession, setPendingSession] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const form = e.currentTarget;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value;
    const password = (form.elements.namedItem("password") as HTMLInputElement)
      .value;

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = (await res.json()) as {
        ok?: boolean;
        challenge?: string;
        session?: string;
        email?: string;
        error?: string;
      };

      if (res.ok && data.challenge === "NEW_PASSWORD_REQUIRED") {
        setPendingEmail(data.email ?? email);
        setPendingSession(data.session ?? "");
        setStage("new_password");
        return;
      }
      if (res.ok) {
        router.push(routes.manage.usage);
        router.refresh();
        return;
      }
      setError(
        res.status === 401 ? t("errorInvalidCredentials") : t("errorUnknown"),
      );
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleNewPassword(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);

    const form = e.currentTarget;
    const newPassword = (
      form.elements.namedItem("newPassword") as HTMLInputElement
    ).value;
    const confirmPassword = (
      form.elements.namedItem("confirmPassword") as HTMLInputElement
    ).value;

    if (newPassword !== confirmPassword) {
      setError(t("errorPasswordMismatch"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: pendingEmail,
          session: pendingSession,
          newPassword,
        }),
      });
      const data = (await res.json()) as { ok?: boolean; error?: string };

      if (res.ok) {
        router.push(routes.manage.usage);
        router.refresh();
        return;
      }
      if (data.error === "session_expired" || res.status === 401) {
        setStage("login");
        setPendingSession("");
        setError(t("errorSessionExpired"));
      } else if (data.error === "invalid_password") {
        setError(t("errorInvalidPassword"));
      } else {
        setError(t("errorUnknown"));
      }
    } catch {
      setError(t("errorUnknown"));
    } finally {
      setSubmitting(false);
    }
  }

  // ── 言語セレクタ ─────────────────────────────────────────────
  const langSelector = (
    <div
      style={{
        position: "absolute",
        top: 32,
        right: 36,
        display: "flex",
        alignItems: "center",
        gap: 4,
        border: "1px solid #e6e6ea",
        borderRadius: 8,
        padding: "8px 14px",
        fontSize: 13,
        color: "#4a4a52",
        background: "#fff",
        cursor: "default",
        userSelect: "none",
      }}
    >
      <select
        value={locale}
        onChange={(e) => {
          setLocaleCookie(e.target.value);
          router.refresh();
        }}
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          border: "none",
          outline: "none",
          background: "transparent",
          fontFamily: "inherit",
          fontSize: 13,
          color: "#4a4a52",
          cursor: "pointer",
        }}
      >
        {Object.entries(languageLabels).map(([code, label]) => (
          <option key={code} value={code}>
            {label}
          </option>
        ))}
      </select>
      <span style={{ fontSize: 10, color: "#9a9aa2", pointerEvents: "none" }}>
        ▾
      </span>
    </div>
  );

  // ── 左カラム（ブランド面） ──────────────────────────────────
  const leftPanel = (
    <div
      className="lf-brand"
      style={{
        flex: "1 1 50%",
        position: "relative",
        background: "#141318",
        color: "#ffffff",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        padding: "56px 56px 52px",
      }}
    >
      {/* ドットパターン */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
          pointerEvents: "none",
        }}
      />
      {/* アクセントグロー */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          background: `radial-gradient(55% 45% at 72% 28%, ${ACCENT} 0%, transparent 66%)`,
          opacity: 0.42,
          pointerEvents: "none",
        }}
      />

      {/* ロゴ */}
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 13,
        }}
      >
        <div
          style={{
            width: 22,
            height: 22,
            background: ACCENT,
            borderRadius: 5,
            transform: "rotate(45deg)",
            flexShrink: 0,
          }}
        />
        <span
          style={{ fontSize: 21, fontWeight: 700, letterSpacing: "-0.01em" }}
        >
          Proomi
        </span>
      </div>

      {/* 見出しブロック */}
      <div style={{ position: "relative", maxWidth: 470 }}>
        <div
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: "0.16em",
            color: ACCENT,
            marginBottom: 22,
          }}
        >
          {t("brandTagline")}
        </div>
        <h1
          style={{
            fontSize: 46,
            lineHeight: 1.3,
            fontWeight: 900,
            letterSpacing: "-0.01em",
            margin: "0 0 24px",
            whiteSpace: "pre-line",
          }}
        >
          {t("brandHeadline")}
        </h1>
        <p
          style={{
            fontSize: 16,
            lineHeight: 1.85,
            color: "rgba(255,255,255,0.6)",
            margin: 0,
            maxWidth: 430,
          }}
        >
          {t("brandSubtext")}
        </p>
      </div>

      {/* 箇条書き */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        {[
          t("featureAiInterview"),
          t("featureConsistent"),
          t("featureReport"),
        ].map((feat) => (
          <div
            key={feat}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              fontSize: 14.5,
              color: "rgba(255,255,255,0.76)",
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: ACCENT,
                flexShrink: 0,
              }}
            />
            {feat}
          </div>
        ))}
      </div>
    </div>
  );

  // ── 右カラム共通ラッパー ────────────────────────────────────
  const rightWrapper = (children: React.ReactNode) => (
    <div
      style={{
        flex: "1 1 50%",
        position: "relative",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        padding: "56px 72px",
        boxSizing: "border-box",
      }}
    >
      {langSelector}
      <div style={{ width: "100%", maxWidth: 380, boxSizing: "border-box" }}>
        {children}
      </div>
    </div>
  );

  // ── 初回ログイン強制パスワード変更 ─────────────────────────
  if (stage === "new_password") {
    return (
      <>
        <style>{PAGE_STYLE}</style>
        <div
          style={{
            display: "flex",
            width: "100%",
            height: "100vh",
            fontFamily: "'Noto Sans JP', sans-serif",
            background: "#ffffff",
            color: "#1c1b22",
            overflow: "hidden",
          }}
        >
          {leftPanel}
          {rightWrapper(
            <form
              style={{ display: "flex", flexDirection: "column", gap: 16 }}
              onSubmit={(e) => void handleNewPassword(e)}
            >
              <div>
                <h2
                  style={{
                    fontSize: 30,
                    fontWeight: 700,
                    margin: "0 0 10px",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {t("newPasswordTitle")}
                </h2>
                <p
                  style={{
                    fontSize: 14.5,
                    color: "#7a7a82",
                    margin: "0 0 34px",
                    lineHeight: 1.6,
                  }}
                >
                  {t("newPasswordDescription")}
                </p>
              </div>
              <div>
                <label htmlFor="newPassword" style={labelStyle}>
                  {t("newPasswordLabel")}
                </label>
                <input
                  id="newPassword"
                  name="newPassword"
                  type="password"
                  required
                  minLength={8}
                  // biome-ignore lint/a11y/noAutofocus: login form UX
                  autoFocus
                  autoComplete="new-password"
                  className="lf-input"
                  style={inputStyle}
                />
              </div>
              <div>
                <label htmlFor="confirmPassword" style={labelStyle}>
                  {t("confirmPasswordLabel")}
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  className="lf-input"
                  style={inputStyle}
                />
              </div>
              {error && (
                <p style={{ margin: 0, fontSize: 13, color: "#ef4444" }}>
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="lf-btn"
                style={{
                  ...btnStyle,
                  opacity: submitting ? 0.5 : 1,
                  cursor: submitting ? "not-allowed" : "pointer",
                }}
              >
                {submitting ? t("submitting") : t("newPasswordSubmit")}
              </button>
            </form>,
          )}
        </div>
      </>
    );
  }

  // ── サインインフォーム ──────────────────────────────────────
  return (
    <>
      <style>{PAGE_STYLE}</style>
      <div
        style={{
          display: "flex",
          width: "100%",
          height: "100vh",
          fontFamily: "'Noto Sans JP', sans-serif",
          background: "#ffffff",
          color: "#1c1b22",
          overflow: "hidden",
        }}
      >
        {leftPanel}
        {rightWrapper(
          <form
            style={{ display: "flex", flexDirection: "column" }}
            onSubmit={(e) => void handleLogin(e)}
          >
            <h2
              style={{
                fontSize: 30,
                fontWeight: 700,
                margin: "0 0 10px",
                letterSpacing: "-0.01em",
              }}
            >
              {t("signIn")}
            </h2>
            <p
              style={{
                fontSize: 14.5,
                color: "#7a7a82",
                margin: "0 0 34px",
                lineHeight: 1.6,
              }}
            >
              {t("signInDescription")}
            </p>

            <label htmlFor="email" style={labelStyle}>
              {t("emailLabel")}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              required
              // biome-ignore lint/a11y/noAutofocus: login form UX
              autoFocus
              autoComplete="email"
              placeholder={t("emailPlaceholder")}
              className="lf-input"
              style={{ ...inputStyle, marginBottom: 22 }}
            />

            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                marginBottom: 8,
              }}
            >
              <label
                htmlFor="password"
                style={{ ...labelStyle, marginBottom: 0 }}
              >
                {t("passwordLabel")}
              </label>
              <Link
                href={routes.auth.forgotPassword}
                style={{
                  fontSize: 12.5,
                  color: ACCENT,
                  textDecoration: "none",
                }}
              >
                {t("forgotPassword")}
              </Link>
            </div>
            <input
              id="password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
              placeholder={t("passwordPlaceholder")}
              className="lf-input"
              style={{ ...inputStyle, marginBottom: 20 }}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.nativeEvent.isComposing) {
                  e.currentTarget.form?.requestSubmit();
                }
              }}
            />

            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 9,
                fontSize: 14,
                color: "#4a4a52",
                marginBottom: 28,
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                name="rememberMe"
                style={{
                  width: 16,
                  height: 16,
                  accentColor: ACCENT,
                  margin: 0,
                  cursor: "pointer",
                }}
              />
              <span>{t("rememberMe")}</span>
            </label>

            {error && (
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "#ef4444" }}>
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="lf-btn"
              style={{
                ...btnStyle,
                opacity: submitting ? 0.5 : 1,
                cursor: submitting ? "not-allowed" : "pointer",
              }}
            >
              {submitting ? t("submitting") : t("signInButton")}
            </button>

            <p
              style={{
                textAlign: "center",
                fontSize: 12.5,
                color: "#9a9aa2",
                margin: "24px 0 0",
              }}
            >
              {t("contactAdmin")}
            </p>
          </form>,
        )}
      </div>
    </>
  );
}
